import React, { useState } from 'react';
import { API_BASE } from '../config';

function PeriodInsights({ chartData, framework = 'parasara', focusArea = 'career' }) {
  const [customQuestions, setCustomQuestions] = useState([]);
  const [newQuestion, setNewQuestion] = useState('');
  const [loadingQuestion, setLoadingQuestion] = useState(null);
  const [error, setError] = useState(null);

  const handleSubmitQuestion = async (e) => {
    e.preventDefault();

    if (!newQuestion.trim()) {
      setError('Please enter a question');
      return;
    }

    if (newQuestion.length < 5) {
      setError('Please ask a more specific question (at least 5 characters)');
      return;
    }

    setError(null);
    setLoadingQuestion(newQuestion);

    try {
      const response = await fetch(`${API_BASE}/api/period-insights-deepseek`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          chartData: chartData,
          framework: framework,
          focusArea: focusArea,
          customQuestion: newQuestion
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        // Fallback to pattern-matching if DeepSeek fails
        if (errorData.error?.includes('API key')) {
          console.warn('DeepSeek API not configured, using fallback pattern-matching');
          return handleFallbackInsights();
        }
        throw new Error(errorData.error || 'Failed to get answer');
      }

      const data = await response.json();

      if (data.answer) {
        setCustomQuestions([
          ...customQuestions,
          {
            id: Date.now(),
            question: newQuestion,
            answer: data.answer,
            source: data.source || 'Vedic Astrology Sources',
            citations: data.citations || [],
            isTherapistMode: data.isTherapistMode,
            confidence: data.confidence,
            method: data.method
          }
        ]);
      } else {
        setError('Could not generate an answer. Please try a different question.');
      }
    } catch (err) {
      console.error('Period insights error:', err);
      setError('Unable to get answer. Make sure the backend is running and DEEPSEEK_API_KEY is set.');
    } finally {
      setLoadingQuestion(null);
      setNewQuestion('');
    }
  };

  const handleFallbackInsights = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/period-insights`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          chartData: chartData,
          framework: framework,
          focusArea: focusArea,
          customQuestion: newQuestion
        })
      });

      if (!response.ok) throw new Error('Fallback also failed');
      const data = await response.json();

      if (data.answer) {
        setCustomQuestions([
          ...customQuestions,
          {
            id: Date.now(),
            question: newQuestion,
            answer: data.answer,
            source: data.source || 'Vedic Astrology Sources',
            citations: data.citations || [],
            isTherapistMode: false,
            confidence: 'medium',
            method: 'pattern-match'
          }
        ]);
      } else {
        setError('Could not generate an answer. Please try a different question.');
      }
    } catch (err) {
      console.error('Fallback insights error:', err);
      setError('Unable to get answer. Please check backend connection.');
    } finally {
      setLoadingQuestion(null);
      setNewQuestion('');
    }
  };

  const focusAreaLabel = {
    career: '💼 Career & Work',
    relationship: '💕 Relationships',
    health: '🧘 Health & Wellness',
    lifePurpose: '✨ Life Purpose',
    others: '❓ Other Questions'
  };

  return (
    <div className="space-y-6">
      {/* Question Input */}
      <div>
        <h3 className="text-lg font-semibold text-white mb-4">
          Ask About {focusAreaLabel[focusArea]}
        </h3>

        <form onSubmit={handleSubmitQuestion} className="space-y-3">
          <div className="relative">
            <input
              type="text"
              value={newQuestion}
              onChange={(e) => setNewQuestion(e.target.value)}
              placeholder="E.g., What should I expect in my career during this period?"
              className="w-full px-4 py-3 bg-white/10 border border-white/30 rounded-lg text-white placeholder-white/50 focus:outline-none focus:border-purple-400 transition"
              disabled={loadingQuestion !== null}
            />
            <button
              type="submit"
              disabled={loadingQuestion !== null || !newQuestion.trim()}
              className="absolute right-2 top-1/2 -translate-y-1/2 px-4 py-2 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-semibold rounded transition"
            >
              {loadingQuestion ? 'Thinking...' : 'Ask'}
            </button>
          </div>

          {error && (
            <div className="text-sm text-red-400 bg-red-500/10 border border-red-500/30 rounded p-2">
              {error}
            </div>
          )}
        </form>
      </div>

      {/* Questions & Answers */}
      {customQuestions.length > 0 ? (
        <div>
          <h4 className="text-sm font-semibold text-white/80 mb-4 uppercase tracking-wide">
            Your Questions ({customQuestions.length})
          </h4>

          <div className="space-y-4">
            {customQuestions.map((qa) => (
              <div
                key={qa.id}
                className="bg-white/5 rounded-lg border border-white/20 overflow-hidden hover:border-white/40 transition"
              >
                {/* Question */}
                <div className="p-4 border-b border-white/10 bg-white/[0.02]">
                  <p className="text-white font-medium">
                    Q: {qa.question}
                  </p>
                </div>

                {/* Answer */}
                <div className="p-4 space-y-3">
                  {/* Badges */}
                  <div className="flex gap-2 flex-wrap">
                    {qa.method === 'deepseek' && (
                      <span className="inline-block px-2 py-1 rounded text-xs bg-blue-500/30 text-blue-200 font-semibold">
                        🤖 DeepSeek AI
                      </span>
                    )}
                    {qa.confidence && (
                      <span className={`inline-block px-2 py-1 rounded text-xs font-semibold ${
                        qa.confidence === 'high'
                          ? 'bg-green-500/30 text-green-200'
                          : 'bg-yellow-500/30 text-yellow-200'
                      }`}>
                        {qa.confidence === 'high' ? '✓ High Confidence' : '⚠ Limited Sources'}
                      </span>
                    )}
                    {qa.isTherapistMode && (
                      <span className="inline-block px-2 py-1 rounded text-xs bg-purple-500/30 text-purple-200 font-semibold">
                        🎭 Honest Answer Mode
                      </span>
                    )}
                  </div>

                  <div className="text-white/90 leading-relaxed text-sm space-y-2">
                    {qa.answer.split('\n\n').map((paragraph, idx) => (
                      <p key={idx}>{paragraph}</p>
                    ))}
                  </div>

                  {/* Source Citation */}
                  {qa.source && (
                    <div className="mt-4 pt-4 border-t border-white/10">
                      <p className="text-xs text-white/60 font-semibold mb-2">Source:</p>
                      <p className="text-xs text-white/70 italic">
                        {qa.source}
                      </p>
                      {qa.citations && qa.citations.length > 0 && (
                        <div className="mt-2 space-y-1">
                          <p className="text-xs text-white/60 font-semibold">Citations:</p>
                          {qa.citations.map((citation, idx) => (
                            <p key={idx} className="text-xs text-white/60">
                              • {typeof citation === 'string' ? citation : `${citation.source}: ${citation.content}`}
                            </p>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="bg-white/5 rounded-lg border border-white/20 p-6 text-center">
          <p className="text-white/70 text-sm">
            No questions yet. Ask your first question about your current period above!
          </p>
        </div>
      )}

      {/* Loading State */}
      {loadingQuestion && (
        <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4 animate-pulse">
          <p className="text-blue-200 text-sm">
            <span className="inline-block mr-2">🔮</span>
            Consulting the charts and PDFs...
          </p>
        </div>
      )}
    </div>
  );
}

export default PeriodInsights;
